import { useState } from 'react'
import type { MapStyle } from '../../api/types'
import { useFilmLength, useFilmRate } from '../../hooks/queries'
import { useTexte } from '../../texte'

/** The settings of the day card's animation (#90; the rate since 1.97.0, the stop since 1.100.0, the stop at a change since 1.150.0). */
export type AnimationValues = Pick<MapStyle, 'daycard_anim' | 'daycard_anim_mode' | 'daycard_anim_seconds' | 'daycard_anim_max' | 'daycard_anim_rate' | 'daycard_anim_pause' | 'daycard_anim_change' | 'daycard_anim_pins'>

/** The built-in defaults, the same as `Settings` on the server. */
export const ANIMATION_DEFAULTS: AnimationValues = {
  daycard_anim: 'on',
  daycard_anim_mode: 'fixed',
  daycard_anim_seconds: 60,
  daycard_anim_max: 300,
  daycard_anim_rate: 6,
  daycard_anim_pause: 10,
  daycard_anim_change: 10,
  daycard_anim_pins: 'on',
}

/** The values of a source that may lack some, each filled from the default. */
export function animationFrom(values: Partial<AnimationValues> | null | undefined): AnimationValues {
  return {
    daycard_anim: values?.daycard_anim ?? ANIMATION_DEFAULTS.daycard_anim,
    daycard_anim_mode: values?.daycard_anim_mode ?? ANIMATION_DEFAULTS.daycard_anim_mode,
    daycard_anim_seconds: values?.daycard_anim_seconds ?? ANIMATION_DEFAULTS.daycard_anim_seconds,
    daycard_anim_max: values?.daycard_anim_max ?? ANIMATION_DEFAULTS.daycard_anim_max,
    daycard_anim_rate: values?.daycard_anim_rate ?? ANIMATION_DEFAULTS.daycard_anim_rate,
    daycard_anim_pause: values?.daycard_anim_pause ?? ANIMATION_DEFAULTS.daycard_anim_pause,
    daycard_anim_change: values?.daycard_anim_change ?? ANIMATION_DEFAULTS.daycard_anim_change,
    daycard_anim_pins: values?.daycard_anim_pins ?? ANIMATION_DEFAULTS.daycard_anim_pins,
  }
}


/**
 * The rates of „Nach Strecke“ in tenths of a second per 10 km (1.97.0). A
 * choice rather than a number field: a decimal comma on a phone keyboard is
 * one more way to type something the server turns down. Two tenths apart on
 * both sides of one second (1.124.0, from use: "auf beiden Seiten in 0,2
 * Schritten: 0,2 0,4 0,6 0,8 1,0, 1,2 1,4 1,6 1.8 2.0"). Offered within the
 * server's bounds (`film_rate`), not trusted as bounds of its own; a value
 * stored before (0,1, 0,3, 0,5 …) stays offered on its card.
 */
export const ANIMATION_RATES = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20] as const

/**
 * The stop at each highlight in tenths of a second (1.100.0, from use: "kurzer
 * Halt (1 sec? - auch einstellbar wie die restlichen Fahrtparameter?)"),
 * within `Settings::DARSTELLUNG_ZAHL` (0 to 100). Five and ten seconds since
 * 1.149.0, from use: "zusätzliche Werte: 5 und 10 sec".
 */
export const ANIMATION_PAUSES = [0, 5, 10, 20, 30, 50, 100] as const

/**
 * The stop where the vehicle changes, in tenths of a second (1.150.0, from
 * use: "beim Fahrzeugwechsel kurz warten (einstellbar - Standard 1 s - von 0
 * bis 5 s)"), within `Settings::DARSTELLUNG_ZAHL` (0 to 50).
 */
export const ANIMATION_CHANGES = [0, 5, 10, 20, 30, 50] as const

/**
 * The fields for the animation of the day card (#90, 1.96.0).
 *
 * **One component for three places**: the administration sets the default
 * for new trips (`KartenfarbenCard`), the author the trip's own copy (the
 * colour drawer of the map, `Kartenregler`), and the day sheet the values for
 * one card (`Tagwahl`). Written more than once, the copies would drift apart
 * the first time a field is added.
 */
export function AnimationFields({
  values,
  onChange,
}: {
  values: AnimationValues
  onChange: (part: Partial<AnimationValues>) => void
}) {
  const t = useTexte().farben.animation
  const on = values.daycard_anim === 'on'
  // The bounds come from the server; until they are there the number waits.
  const length = useFilmLength()
  const rateBounds = useFilmRate()
  // The list within the server's bounds, and the card's own value even when it is not in the list.
  const rates = [...new Set([...ANIMATION_RATES.filter((r) => rateBounds === null || (r >= rateBounds.min && r <= rateBounds.max)), values.daycard_anim_rate])].sort((a, b) => a - b)
  // The longest film this card allows (1.122.0): the fixed length cannot ask for more.
  const most = length === null ? null : Math.min(length.max, Math.max(length.min, values.daycard_anim_max))
  return (
    <div className="animation-fields" data-animation-fields="">
      <label className="kartenregler__nadel">
        <input
          type="checkbox"
          name="daycard_anim"
          checked={on}
          onChange={(e) => onChange({ daycard_anim: e.target.checked ? 'on' : 'off' })}
        />
        <span>{t.on}</span>
      </label>
      <fieldset className="animation-fields__length" disabled={!on}>
        <legend className="field__label">{t.length}</legend>
        <label className="kartenregler__nadel">
          <input
            type="radio"
            name="daycard_anim_mode"
            value="fixed"
            checked={values.daycard_anim_mode !== 'distance'}
            onChange={() => onChange({ daycard_anim_mode: values.daycard_anim_mode === 'time' ? 'time' : 'fixed' })}
          />
          <span>{t.fixed}</span>
          <SecondsField
            name="daycard_anim_seconds"
            value={values.daycard_anim_seconds}
            min={length?.min ?? null}
            max={most}
            label={t.seconds}
            onCommit={(seconds) => onChange({ daycard_anim_seconds: seconds })}
          />
          <span>{t.secondsShort}</span>
        </label>
        {/* **Nach Fahrzeit** (1.189.0, from use: "Dauer / Geschwindigkeit aus
            der Aufzeichnung verwenden und relativ anpassen"): the length of
            „Fest“, shared by the recording's own time, and inside a section
            the vehicle follows it. Since 1.189.1 a box under „Fest“ and not
            a third choice (from use: "als Filmdauer die feste Zeit genommen
            … von der Bedienung inkonsequent"): the length stands where it
            applies. The mode stays `time` on the server. */}
        <label className="kartenregler__nadel animation-fields__sub">
          <input
            type="checkbox"
            name="daycard_anim_by_time"
            checked={values.daycard_anim_mode === 'time'}
            disabled={values.daycard_anim_mode === 'distance'}
            onChange={(e) => onChange({ daycard_anim_mode: e.target.checked ? 'time' : 'fixed' })}
          />
          <span>{t.byTime}</span>
        </label>
        <label className="kartenregler__nadel">
          <input
            type="radio"
            name="daycard_anim_mode"
            value="distance"
            checked={values.daycard_anim_mode === 'distance'}
            onChange={() => onChange({ daycard_anim_mode: 'distance' })}
          />
          <span>{t.byDistance}</span>
          <select
            className="field__input animation-fields__rate"
            name="daycard_anim_rate"
            value={values.daycard_anim_rate}
            aria-label={t.rate}
            onChange={(e) => onChange({ daycard_anim_rate: Number(e.target.value) })}
          >
            {rates.map((tenths) => (
              <option key={tenths} value={tenths}>{t.rateValue(tenths)}</option>
            ))}
          </select>
          <span>{t.rateUnit}</span>
        </label>
        {/* The longest film (1.122.0, from use: "Obergrenze einstellbar
            machen, an den gleichen Stellen in denen die anderen
            Videoparameter stehen"): it holds for both ways above, the fixed
            length slowed by its sections and „Nach Strecke“. Lowered under
            the fixed length, it takes the length down with it, so the two
            numbers never say different things. */}
        <label className="kartenregler__nadel">
          <span>{t.most}</span>
          <SecondsField
            name="daycard_anim_max"
            value={values.daycard_anim_max}
            min={length?.min ?? null}
            max={length?.max ?? null}
            label={t.mostLabel}
            onCommit={(ceiling) =>
              onChange(values.daycard_anim_seconds > ceiling ? { daycard_anim_max: ceiling, daycard_anim_seconds: ceiling } : { daycard_anim_max: ceiling })
            }
          />
          <span>{t.secondsShort}</span>
        </label>
      </fieldset>
      {on && values.daycard_anim_mode === 'fixed' && length !== null && most !== null && (
        <p className="leise" data-fixed-speed-hint="">{t.fixedSpeedHint(length.min, most)}</p>
      )}
      {on && values.daycard_anim_mode === 'time' && length !== null && most !== null && (
        <p className="leise" data-time-hint="">{t.byTimeHint(values.daycard_anim_seconds, most)}</p>
      )}
      <label className="kartenregler__nadel">
        <input
          type="checkbox"
          name="daycard_anim_pins"
          checked={values.daycard_anim_pins === 'on'}
          disabled={!on}
          onChange={(e) => onChange({ daycard_anim_pins: e.target.checked ? 'on' : 'off' })}
        />
        <span>{t.pins}</span>
      </label>
      {/* The stop belongs to the pins that appear when passing: without them
          the vehicle passes nothing it could stop at. */}
      <label className="kartenregler__nadel">
        <span>{t.pause}</span>
        <select
          className="field__input animation-fields__pause"
          name="daycard_anim_pause"
          value={values.daycard_anim_pause}
          disabled={!on || values.daycard_anim_pins !== 'on'}
          onChange={(e) => onChange({ daycard_anim_pause: Number(e.target.value) })}
        >
          {ANIMATION_PAUSES.map((tenths) => (
            <option key={tenths} value={tenths}>{t.pauseValue(tenths)}</option>
          ))}
        </select>
      </label>
      {/* Not tied to the pins: a change of vehicle is in every film that has one. */}
      <label className="kartenregler__nadel">
        <span>{t.change}</span>
        <select
          className="field__input animation-fields__pause"
          name="daycard_anim_change"
          value={values.daycard_anim_change}
          disabled={!on}
          onChange={(e) => onChange({ daycard_anim_change: Number(e.target.value) })}
        >
          {ANIMATION_CHANGES.map((tenths) => (
            <option key={tenths} value={tenths}>{t.pauseValue(tenths)}</option>
          ))}
        </select>
      </label>
    </div>
  )
}

/**
 * A number of seconds, typed freely and bounded only when the field is left
 * or Enter is pressed (1.124.0). From use, with a recording: "wie kann ich 30
 * sek eingeben?" -- both fields cut every keystroke to their bounds, so the
 * "3" of 30 became 5 and the "0" made it 50; in "Höchstens" the 5 also took
 * the length down with it. While typing, the field holds what was typed; a
 * value from outside (another card, the server) replaces it only when the
 * field is not being edited. Empty or not a number on leaving: back to the
 * value before. One component for both fields, so the next one does not get
 * the old way again.
 */
function SecondsField({
  name,
  value,
  min,
  max,
  label,
  onCommit,
}: {
  name: string
  value: number
  min: number | null
  max: number | null
  label: string
  onCommit: (seconds: number) => void
}) {
  // `null` while nobody types: then the field shows the value from outside.
  const [draft, setDraft] = useState<string | null>(null)
  const commit = () => {
    if (draft === null) return
    const number = Number(draft.replace(',', '.'))
    setDraft(null)
    if (draft.trim() === '' || !Number.isFinite(number) || min === null || max === null) return
    const bounded = Math.min(max, Math.max(min, Math.round(number)))
    if (bounded !== value) onCommit(bounded)
  }
  return (
    <input
      className="field__input animation-fields__seconds"
      type="number"
      inputMode="numeric"
      name={name}
      min={min ?? undefined}
      max={max ?? undefined}
      disabled={min === null || max === null}
      value={draft ?? String(value)}
      aria-label={label}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
      }}
    />
  )
}
